import { Application, TextureSource } from 'pixi.js';
import { FpsMeter } from './debug/fpsMeter';
import { StageScaler } from './render/StageScaler';
import { GrayboxScene } from './scenes/GrayboxScene';
import { SceneManager } from './scenes/SceneManager';
import { TitleScene } from './scenes/TitleScene';
import { createTrainingScene } from './scenes/TrainingSceneFactory';
import { isMinigameId } from './sim/minigames';
import { disablePersistence } from './sim/ranch/save';
import { RACE_TUNING } from './sim/race/tuning';
import { mountTuningPanel } from './ui/TuningPanel';
import { IS_WEB } from './variant';

async function boot(): Promise<void> {
  TextureSource.defaultOptions.scaleMode = 'nearest';
  const app = new Application();
  await app.init({
    resizeTo: window,
    antialias: false,
    resolution: window.devicePixelRatio || 1,
    autoDensity: true,
    roundPixels: true,
    background: 0x14100c,
    preference: 'webgl',
  });
  document.querySelector('#app')!.append(app.canvas);

  const params = new URLSearchParams(location.search);
  const debug = params.has('debug');
  const seedParam = params.get('seed');

  const uiRoot = document.querySelector<HTMLElement>('#ui-root')!;
  const scaler = new StageScaler(app);
  const fps = debug ? new FpsMeter(uiRoot, true) : null;
  if (debug) mountTuningPanel(uiRoot);

  const scenes = new SceneManager();
  scenes.ctx = { app, scaler, uiRoot, fps, scenes };

  const seed = seedParam ? Number(seedParam) >>> 0 : null;
  const qaMode = params.has('qa');
  if (qaMode) disablePersistence();
  // The web build never touches game saves; dev and desktop behave like
  // a full install.
  const canPersist = !qaMode && !IS_WEB;

  const drillParam = params.get('drill');
  if (qaMode && isMinigameId(drillParam)) {
    const tier = Math.max(1, Math.min(4, Number(params.get('tier')) || 2));
    scenes.push(createTrainingScene(
      drillParam,
      { seed: seed ?? 1337, tier, xpMult: 1 },
      {
        onDone: () => scenes.push(new TitleScene({ canPersist: false, forceNew: true, seed })),
      },
    ));
  } else if (params.has('gallery')) {
    const { mountGallery } = await import('./art/gallery');
    mountGallery(uiRoot);
  } else if (params.has('graybox')) {
    scenes.push(new GrayboxScene(seed));
  } else {
    scenes.push(new TitleScene({ canPersist, forceNew: params.has('new'), seed }));
  }

  // One-shot params don't survive into reloads.
  if (params.has('new')) {
    params.delete('new');
    const query = params.toString();
    history.replaceState(null, '', `${location.pathname}${query ? `?${query}` : ''}`);
  }

  app.ticker.add(() => scenes.frame(performance.now()));

  if (debug) {
    (window as { __derby?: unknown }).__derby = { scenes, tuning: RACE_TUNING };
  }
}

void boot();
